import React, { useState } from 'react';
import { Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThreeHero } from '../components/ThreeHero';
import { SearchBar } from '../components/SearchBar';
import { TopToolsSection } from '../components/TopToolsSection';
import { NewToolsSection } from '../components/NewToolsSection';
import { FilterSidebar } from '../components/FilterSidebar';
import { Header } from '../components/Header';
import { DonateModal } from '../components/DonateModal';
import { useTools } from '../hooks/useTools';
import { useFilters } from '../hooks/useFilters';
import { Heart } from 'lucide-react';
export function HomePage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  // Get tools and filtering
  const {
    tools,
    loading
  } = useTools();
  const {
    filters,
    filteredTools,
    availableCategories,
    togglePricing,
    toggleCategory,
    setMinRating,
    clearFilters,
    activeFilterCount
  } = useFilters(tools);
  return <div className="min-h-screen bg-black text-white selection:bg-cyan-500/30 selection:text-cyan-200 overflow-hidden font-sans" data-id="element-1105">
      {/* Fixed Background Elements - Improved gradients */}
      <div className="fixed inset-0 z-0 pointer-events-none" data-id="element-1106">
        {/* Modern gradient blobs */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full blur-[120px]" data-id="element-1107" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-purple-500/10 to-pink-500/10 rounded-full blur-[120px]" data-id="element-1108" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-orange-500/5 to-yellow-500/5 rounded-full blur-[150px]" data-id="element-1109" />

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
        backgroundSize: '100px 100px'
      }} data-id="element-1110" />
      </div>

      <Header data-id="element-1111" />

      {/* Filter Sidebar */}
      <FilterSidebar isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} filters={filters} availableCategories={availableCategories} togglePricing={togglePricing} toggleCategory={toggleCategory} setMinRating={setMinRating} clearFilters={clearFilters} activeFilterCount={activeFilterCount} filteredCount={filteredTools.length} totalCount={tools.length} data-id="element-1112" />

      {/* Floating Filter Button - Desktop only */}
      <motion.button initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.5,
      type: 'spring',
      stiffness: 300
    }} onClick={() => setIsFilterOpen(!isFilterOpen)} className="hidden md:flex fixed left-4 bottom-4 lg:left-6 lg:bottom-6 z-30 p-3 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 text-white shadow-2xl hover:shadow-cyan-500/50 transition-all hover:scale-110 group" data-id="element-1113">
        <Filter size={20} className="group-hover:rotate-12 transition-transform" data-id="element-1114" />
        {activeFilterCount > 0 && <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-xs font-bold" data-id="element-1115">
            {activeFilterCount}
          </motion.div>}
      </motion.button>

      <div className="relative z-10 pt-16" data-id="element-1116">
        <main className="pb-12 md:pb-32" data-id="element-1117">
          <ThreeHero data-id="element-1118" />

          {/* Search Bar */}
          <SearchBar data-id="element-1119" />

          {/* Top Tools - Always shows 4 tools, not affected by filters */}
          <TopToolsSection data-id="element-1120" />

          {/* More AI Tools - Now uses filtered tools */}
          <NewToolsSection tools={filteredTools} loading={loading} activeCategory={activeCategory} setActiveCategory={setActiveCategory} onFilterClick={() => setIsFilterOpen(!isFilterOpen)} activeFilterCount={activeFilterCount} selectedCategories={filters.categories} data-id="element-1121" />
        </main>

        <footer className="border-t border-white/10 py-6 bg-black/80 backdrop-blur-lg" data-id="element-1122">
          <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-1123">
            <div className="flex items-center gap-2" data-id="element-1124">
              <span style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontWeight: 300,
              fontSize: '1.125rem',
              lineHeight: '1.75rem',
              letterSpacing: '.15em'
            }} className="text-white" data-id="element-1125">
                FAIND
              </span>
            </div>
            <div className="text-gray-500 text-sm" data-id="element-1126">
              © {currentYear} Faind. All rights reserved.
            </div>
            <div className="flex items-center gap-6" data-id="element-1127">
              <a href="https://x.com/faindyourapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Follow us on X" data-id="element-1128">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" data-id="element-1129">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-1130" />
                </svg>
              </a>
              <button onClick={() => setIsDonateModalOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-pink-500/30 text-gray-400 hover:text-pink-400 transition-all text-sm font-medium" data-id="element-1131">
                <Heart size={14} data-id="element-1132" />
                Donate
              </button>
            </div>
          </div>
        </footer>

        <DonateModal isOpen={isDonateModalOpen} onClose={() => setIsDonateModalOpen(false)} data-id="element-1133" />
      </div>
    </div>;
}